import { createClient, SupabaseClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  console.error('Missing Supabase credentials:', {
    url: supabaseUrl ? 'Present' : 'Missing',
    key: supabaseAnonKey ? 'Present' : 'Missing'
  });
  throw new Error('Missing Supabase environment variables');
}

// Validate URL format
try {
  new URL(supabaseUrl);
} catch (error) {
  console.error('Invalid Supabase URL format:', supabaseUrl);
  throw new Error('Invalid Supabase URL configuration');
}

let supabaseClient: SupabaseClient;

try {
  supabaseClient = createClient(supabaseUrl, supabaseAnonKey, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      storage: typeof window !== 'undefined' ? window.localStorage : undefined,
      flowType: 'pkce',
    },
    global: {
      headers: {
        'X-Client-Info': 'pepcoin-web',
      },
    },
    db: {
      schema: 'public',
    },
  });
  
  console.log('✅ Supabase client initialized successfully');
} catch (error) {
  console.error('❌ Failed to initialize Supabase client:', error);
  throw error;
}

export const supabase = supabaseClient;

// Session refresh handler
let refreshPromise: Promise<any> | null = null;

export const refreshSession = async () => {
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = (async () => {
    try {
      const { data, error } = await supabase.auth.refreshSession();
      
      if (error) {
        console.error('Session refresh failed:', error);
        await supabase.auth.signOut();
        return null;
      }
      
      return data.session;
    } catch (error) {
      console.error('Session refresh error:', error);
      await supabase.auth.signOut();
      return null;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

// Get valid session with auto-refresh
export const getValidSession = async () => {
  try {
    const { data: { session }, error } = await supabase.auth.getSession();
    
    if (error) {
      console.error('Get session error:', error);
      return null;
    }
    
    if (!session) {
      return null;
    }
    
    // Check if token is expired or about to expire (within 5 minutes)
    const expiresAt = session.expires_at ? session.expires_at * 1000 : 0;
    const now = Date.now();
    const fiveMinutes = 5 * 60 * 1000;
    
    if (expiresAt - now < fiveMinutes) {
      console.log('Token expiring soon, refreshing...');
      return await refreshSession();
    }
    
    return session;
  } catch (error) {
    console.error('Get valid session error:', error);
    return null;
  }
};

// Enhanced connection health check with timeout
export const checkSupabaseConnection = async (): Promise<{ connected: boolean; error?: string }> => {
  try {
    // Create a timeout promise
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => reject(new Error('Connection timeout')), 10000);
    });

    // Race between the query and timeout
    const queryPromise = supabase
      .from('profiles')
      .select('count', { count: 'exact', head: true })
      .limit(1);

    const { error } = await Promise.race([queryPromise, timeoutPromise]);
    
    if (error) {
      console.error('Connection check failed:', error);
      return { connected: false, error: error.message };
    }
    
    return { connected: true };
  } catch (error: any) {
    console.error('Connection check error:', error);
    return { 
      connected: false, 
      error: error.message || 'Network error' 
    };
  }
};

// Listen for auth state changes
supabase.auth.onAuthStateChange(async (event, session) => {
  if (event === 'SIGNED_IN') {
    console.log('✅ User signed in successfully');
  } else if (event === 'SIGNED_OUT') {
    console.log('🚪 User signed out');
    localStorage.clear();
    sessionStorage.clear();
  } else if (event === 'TOKEN_REFRESHED') {
    console.log('✅ Token refreshed successfully');
  } else if (event === 'USER_UPDATED') {
    console.log('✅ User data updated');
  }
});
